import { useQuery } from '@tanstack/react-query';

import type { LeadPipelineConfig } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { leadsEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

export interface PipelinesResponse {
	[key: string]: LeadPipelineConfig;
}

/**
 * API function to get all available pipelines for a services provider
 */
export const getPipelines = async (servicesProviderId: string): Promise<PipelinesResponse> => {
	const response = await apiClient.get<PipelinesResponse>(
		leadsEndpoints.pipelines(servicesProviderId),
	);
	return response.data;
};

/**
 * Hook for fetching pipelines configuration
 * @returns Query result with all available pipelines and their stage definitions
 * @example
 * ```tsx
 * const { data: pipelines } = useGetPipelines();
 * const inboundPipeline = pipelines?.INBOUND_PIPELINE;
 * ```
 */
export const useGetPipelines = () => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const currentServicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.leads.pipelines(currentServicesProviderId).queryKey,
		queryFn: () => {
			if (!currentServicesProviderId) {
				throw new Error('Unable to fetch pipelines without a services provider ID.');
			}

			return getPipelines(currentServicesProviderId);
		},
		enabled: Boolean(currentServicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};
